/* Mirai agent commerce, 2026-09-22. Uses Shopify's supported action configuration. */
(() => {
  'use strict';
  const root = (window.MiraiMarketConfig?.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const readJSON = async (url) => {
    const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(`Storefront request failed (${response.status}). Read the cart before retrying an uncertain add.`);
    return response.json();
  };
  async function refreshCart() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }));
    const unique = [...new Set(sections)].slice(0, 5);
    const cart = await readJSON(root + 'cart.js');
    if (unique.length) {
      const sectionURL = new URL(location.href);
      sectionURL.searchParams.set('sections', unique.join(','));
      cart.sections = await readJSON(sectionURL.pathname + sectionURL.search);
    }
    if (window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart, source: 'mirai-standard-action' });
    }
    document.querySelectorAll('cart-count').forEach(el => { el.textContent = cart.item_count; el.hidden = !cart.item_count; });
    document.documentElement.classList.toggle('cart-has-items', cart.item_count > 0);
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
    return cart;
  }
  function configureActions() {
    const actions = window.Shopify?.actions;
    if (!actions?.updateCart?.configure || window.__miraiActionsConfigured) return false;
    actions.updateCart.configure({
      async handler(defaultHandler) {
        const result = await defaultHandler();
        if (!result.userErrors?.length) {
          try { await refreshCart(); }
          catch (error) {
            // A committed mutation must not look like a failed add and encourage a duplicate retry.
            console.warn('Mirai cart display refresh failed; mutation result preserved.', error);
            return { ...result, detail: { ...result.detail, miraiDisplayRefresh: 'failed', cartURL: root + 'cart' } };
          }
        }
        return { ...result, detail: { ...result.detail, handledBy: 'mirai-theme' } };
      }
    });
    // Preserve Shopify's default openCart unless the theme has an actual drawer to open.
    if (document.querySelector('cart-drawer')?.show && actions.openCart?.configure) {
      actions.openCart.configure({ handler() { document.querySelector('cart-drawer')?.show(); } });
    }
    window.__miraiActionsConfigured = true;
    return true;
  }
  function registerMarketTool() {
    const context = document.modelContext || navigator.modelContext;
    if (!context?.registerTool || window.__miraiMarketToolRegistered) return;
    try {
      context.registerTool({
        name: 'get_market_product',
        description: 'Read exact product variants, one-time prices, currency and availability in the CURRENT storefront country. Use after identifying a product handle, especially when native catalog tools quote a different currency. Read-only; does not change country, cart or purchase mode. Final shipping and taxes require checkout.',
        inputSchema: { type: 'object', properties: { handle: { type: 'string', description: 'Exact product handle from its /products/ URL, without slashes or query parameters.' } }, required: ['handle'], additionalProperties: false },
        annotations: { readOnlyHint: true },
        async execute({ handle }) {
          if (typeof handle !== 'string' || !/^[a-z0-9][a-z0-9-]{0,240}$/.test(handle)) throw new Error('Provide one exact product handle.');
          const [product, cart] = await Promise.all([readJSON(root + 'products/' + encodeURIComponent(handle) + '.js'), readJSON(root + 'cart.js')]);
          const cfg = window.MiraiMarketConfig || {};
          if (cfg.currency && cfg.currency !== cart.currency) throw new Error('The active market changed. Reload the product page and read its offer again.');
          const result = {
            country: cfg.country || window.Shopify?.country || null, currency: cart.currency, language: cfg.locale || window.Shopify?.locale || null,
            productId: 'gid://shopify/Product/' + product.id, handle: product.handle, title: product.title,
            url: new URL(root + 'products/' + product.handle, location.origin).href,
            priceSource: 'Shopify AJAX product endpoint in current browser market', purchaseType: 'one_time',
            subscriptionOffersSeparate: (product.selling_plan_groups || []).length > 0,
            finalShippingAndTaxes: 'Calculated at checkout; additional import charges may apply.',
            variants: product.variants.map(v => ({
              variantId: 'gid://shopify/ProductVariant/' + v.id, title: v.title, options: v.options, available: v.available,
              price: { amount: (v.price / 100).toFixed(2), currencyCode: cart.currency },
              url: new URL(root + 'products/' + product.handle + '?variant=' + v.id, location.origin).href
            }))
          };
          return { content: [{ type: 'text', text: JSON.stringify(result) }] };
        }
      });
      window.__miraiMarketToolRegistered = true;
    } catch (error) { console.warn('Mirai market tool registration unavailable.', error); }
  }
  function init() {
    configureActions(); registerMarketTool();
    let tries = 0;
    const retry = setInterval(() => { if (configureActions() || ++tries >= 20) clearInterval(retry); }, 250);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
